/**
 * El chrome de la vitrina: header, pie y la piel tipográfica que envuelven a TODA página
 * pública de marketing (FEATURE-178).
 *
 * Antes el header y el pie eran funciones privadas de `LandingPage.tsx`, así que ninguna
 * segunda página de vitrina podía usarlos —el experimento que validó estas páginas tuvo
 * que duplicarlos para poder mirarse—. Acá viven una sola vez y la landing los consume.
 *
 * Las dos excepciones declaradas de la superficie viajan con el shell, porque son de la
 * vitrina entera y no de la home: **tema oscuro fijo** (ADR-004 §2.6) y **display propia,
 * Archivo Black** (ADR-004 §3.1). La fuente se carga acá y no en el layout para que solo
 * la pague quien abre una página de vitrina.
 *
 * **`interior`** cambia a dónde apuntan el logo y el menú. En la home las entradas son
 * anclas (`#planes`); en una página interior esas secciones no existen, así que vuelven a
 * la home con su ancla (`/es#planes`). Sin eso el menú de una página legal apunta al vacío.
 */
import type { CSSProperties, ReactNode } from "react";
import { Archivo_Black } from "next/font/google";
import { Boton, Brochazo, Container, Logo } from "@/components/brand/pincel";
import { CORREO_CONTACTO, NAV } from "@/components/brand/mock";
import { isLegalPagesEnabled, isRegistrationEnabled } from "@/lib/feature-flags";
import { LEGAL } from "@/lib/legal";

/** Destino de los CTA de conversión. Sin registro abierto, la puerta es el login. */
export const DESTINO_CTA = isRegistrationEnabled ? "/es/register" : "/es/login";

const displayMarketing = Archivo_Black({
  weight: "400",
  subsets: ["latin"],
  variable: "--font-marketing-display",
  display: "swap",
});

/**
 * La display de la vitrina, puesta **directa** y no a través de `var(--font-marketing-display)`.
 *
 * La indirección no resolvía: `--font-display-family` quedaba apuntando a una variable que
 * el árbol no tenía definida, se invalidaba, y `.font-display` —que declara
 * `var(--font-display-family), var(--font-body-family), …`— caía al cuerpo. El titular de
 * la vitrina salía en Archivo en vez de Archivo Black y nada fallaba: compila, tipa, y
 * ninguna auditoría mira tipografía. `style.fontFamily` entrega la familia ya resuelta y
 * no depende de que la clase del módulo llegue al nodo.
 */
const PIEL_TIPOGRAFICA = { "--font-display-family": displayMarketing.style.fontFamily } as CSSProperties;

/**
 * Las clases propias del kit. Viven acá y no en `globals.css` porque son de esta
 * superficie; si se borra la regla y quedan los usos, los elementos caen a la fuente del
 * body sin que nada falle — lo cubre `anti-deriva.test.ts` («clases de proyecto huérfanas»).
 */
const CSS = `
.v4-sombra { text-shadow: 0.055em 0.065em 0 rgb(0 0 0 / 0.45); }

.v4-live { animation: v4-live 1.4s ease-in-out infinite; }
@keyframes v4-live { 0%, 100% { opacity: 1 } 50% { opacity: 0.2 } }

.v4-grow { animation: v4-grow 1.1s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
@keyframes v4-grow { from { width: 0 } }

@media (prefers-reduced-motion: reduce) {
  .v4-live, .v4-grow { animation: none }
}
`;

/**
 * Una página o un dato de la vitrina que todavía no existe. Se ve el hueco en vez de
 * taparlo.
 *
 * Ojo con el alcance: esto es para **páginas** que faltan (los legales con el flag
 * apagado). Los **datos** legales ausentes NO usan esto — salen en blanco, y de eso se
 * encarga el renderer del contenido (FEATURE-178 RF-04).
 */
export function Falta({ children }: { children: ReactNode }) {
  return (
    <span
      className="rounded px-1 py-0.5 text-[0.95em] font-semibold [box-decoration-break:clone]"
      style={{ background: "rgb(255 123 147 / 0.14)", color: "var(--ld-loss)" }}
      title="Pendiente de definir"
    >
      {children}
    </span>
  );
}

/* ══ Header ════════════════════════════════════════════════════════════════ */

function Header({ interior }: { interior: boolean }) {
  const base = interior ? "/es" : "";
  return (
    <header className="sticky top-0 z-50 border-b border-line bg-bg/90 backdrop-blur">
      <Container className="flex h-16 items-center justify-between gap-4">
        <a href={interior ? "/es" : "#"} aria-label="La Tablapp — inicio" className="inline-flex items-center py-2">
          <Logo className="h-6 sm:h-7" />
        </a>
        <nav className="hidden items-center gap-7 md:flex">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={`${base}${n.href}`}
              className="font-display text-[11px] uppercase tracking-[0.18em] text-muted transition hover:text-ink"
            >
              {n.label}
            </a>
          ))}
        </nav>
        <Boton href={DESTINO_CTA} className="h-10 px-5 text-[11px]">
          Comenzar
        </Boton>
      </Container>
    </header>
  );
}

/* ══ Pie ═══════════════════════════════════════════════════════════════════ */

/**
 * Producto · Cuenta · Legal, y abajo la línea de identidad legal.
 *
 * Las entradas legales dependen del flag: con las páginas apagadas siguen marcadas y sin
 * destino, igual que antes de FEATURE-178, porque enlazar a un placeholder es el mismo
 * link muerto con otra cara. Con el flag encendido enlazan, y «Preguntas frecuentes»
 * aparece como cuarta entrada de Producto.
 */
const COLUMNAS_PIE: {
  titulo: string;
  links: { label: string; href?: string; soloConPaginas?: boolean }[];
}[] = [
  {
    titulo: "Producto",
    links: [
      { label: "Vista pública", href: "/es#vitrina" },
      { label: "Cómo funciona", href: "/es#como-funciona" },
      { label: "Planes", href: "/es#planes" },
      { label: "Preguntas frecuentes", href: "/es/faq", soloConPaginas: true },
    ],
  },
  {
    titulo: "Cuenta",
    links: [
      { label: "Entrar", href: "/es/login" },
      { label: "Crear cuenta", href: DESTINO_CTA },
    ],
  },
  {
    titulo: "Legal",
    links: [
      { label: "Términos y Condiciones", href: "/es/terms", soloConPaginas: true },
      { label: "Privacidad", href: "/es/privacy", soloConPaginas: true },
      { label: "Contacto", href: `mailto:${CORREO_CONTACTO}` },
    ],
  },
];

/** Preguntas frecuentes no existe si no hay páginas; las legales, sin ellas, van marcadas. */
const PENDIENTE_SE_MARCA = new Set(["Términos y Condiciones", "Privacidad"]);

function Footer() {
  return (
    <footer className="relative overflow-hidden border-t-2 border-line bg-panel/50 pb-8 pt-12">
      <Brochazo tono="accent" className="absolute -left-10 -top-4 h-10 w-72 -rotate-3 opacity-20" />
      <Container className="relative">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="flex flex-col items-start gap-3">
            <Logo className="h-6" />
            <p className="font-display text-[11px] uppercase tracking-[0.22em] text-muted">Todo en una sola app</p>
            <p className="max-w-xs text-xs leading-relaxed text-muted">
              Tabla, fixture, goleadores y el acta en vivo para tu liga de fútbol amateur.
            </p>
          </div>

          {COLUMNAS_PIE.map((col) => (
            <div key={col.titulo}>
              <p className="font-display text-[11px] uppercase tracking-[0.22em] text-ink/60">{col.titulo}</p>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => {
                  if (l.soloConPaginas && !isLegalPagesEnabled) {
                    // Sin páginas: las legales quedan marcadas; la de ayuda no existe.
                    return PENDIENTE_SE_MARCA.has(l.label) ? (
                      <li key={l.label} className="text-sm">
                        <Falta>{l.label}</Falta>
                      </li>
                    ) : null;
                  }
                  return (
                    <li key={l.label} className="text-sm">
                      <a href={l.href} className="text-muted transition hover:text-ink">
                        {l.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 border-t border-line pt-6">
          {/* La razón social sale en blanco si no está definida (FEATURE-178 §14.1): sin
              ella la línea es solo el copyright, sin puntuación colgando. */}
          <p className="text-xs text-muted">
            © 2026 La Tablapp{LEGAL.entityName ? ` · ${LEGAL.entityName}` : ""}
          </p>
        </div>
      </Container>
    </footer>
  );
}

/* ══ Shell ═════════════════════════════════════════════════════════════════ */

export function VitrinaShell({ children, interior = false }: { children: ReactNode; interior?: boolean }) {
  return (
    <div
      data-theme="dark"
      style={PIEL_TIPOGRAFICA}
      className={`${displayMarketing.variable} flex min-h-screen flex-col bg-bg text-ink antialiased`}
    >
      <style>{CSS}</style>
      <Header interior={interior} />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
